import { prop } from '@ipfabric/utils/fp/prop';
import { useGraphContextSelectors } from 'components/Graph/GraphContext';
import { GraphToolbarDropdown } from 'components/Graph/GraphToolbar/GraphToolbarDropdown';
import { selectGraphToolbarDisabled } from 'components/Graph/GraphToolbar/selectors/selectGraphToolbarDisabled';
import { collapseNotSelectedNodes } from 'components/Graph/commands/collapsing/collapseNotSelectedNodes';
import { collapseSelectedNodes } from 'components/Graph/commands/collapsing/collapseSelectedNodes';
import { uncollapseAllNodes } from 'components/Graph/commands/collapsing/uncollapseAllNodes';
import { uncollapseSelectedNodes } from 'components/Graph/commands/collapsing/uncollapseSelectedNodes';
import { selectPathLookupApplied } from 'components/Graph/model/selectors/graphMetadata/selectPathLookupApplied';
import { selectCollapsedNodeGroupIds } from 'components/Graph/model/selectors/selectCollapsedNodeGroupIds';
import { selectNotSelectedHiddenNodeIds } from 'components/Graph/model/selectors/selectNotSelectedHiddenNodeIds';
import { selectNotSelectedVisibleNodeIds } from 'components/Graph/model/selectors/selectNotSelectedVisibleNodeIds';
import { selectSelectedHiddenNodeIds } from 'components/Graph/model/selectors/selectSelectedHiddenNodeIds';
import { selectSelectedVisibleNodeIds } from 'components/Graph/model/selectors/selectSelectedVisibleNodeIds';
import { CollapseNodesIcon } from 'components/icons/CollapseNodesIcon';
import { useLocale } from 'containers/App/LocaleContext';
import { memo } from 'react';
import type { FC } from 'react';

const BareGraphToolbarCollapseMenu: FC = () => {
  const msg = useLocale();
  const ctxProps = useGraphContextSelectors({
    dispatch: prop('dispatch'),
    selectedHiddenNodeIds: selectSelectedHiddenNodeIds,
    notSelectedHiddenNodeIds: selectNotSelectedHiddenNodeIds,
    selectedVisibleNodeIds: selectSelectedVisibleNodeIds,
    notSelectedVisibleNodeIds: selectNotSelectedVisibleNodeIds,
    collapsedNodeGroupIds: selectCollapsedNodeGroupIds,
    pathLookupApplied: selectPathLookupApplied,
    peekingAtHiddenNodes: prop('peekingAtHiddenNodes'),
    selectedNodeIds: prop('graph', 'selectedNodeIds'),
    toolbarDisabled: selectGraphToolbarDisabled,
  });

  const hasCollapsedNodes = ctxProps.collapsedNodeGroupIds.length > 0;
  const collapseNotSelectedDisabledDueToHiddenNodes =
    ctxProps.selectedHiddenNodeIds.size > 0 ||
    (ctxProps.peekingAtHiddenNodes &&
      ctxProps.notSelectedHiddenNodeIds.size > 0);

  const collapseNotSelectedDisabled =
    ctxProps.toolbarDisabled ||
    ctxProps.pathLookupApplied ||
    ctxProps.selectedNodeIds.size === 0 ||
    ctxProps.notSelectedVisibleNodeIds.size < 2 ||
    collapseNotSelectedDisabledDueToHiddenNodes;

  const getDisabledTitle = () => {
    if (ctxProps.toolbarDisabled) {
      return msg.canvasGraph.graphToolbar.buttons.selectDataToDisplay;
    }
    if (ctxProps.pathLookupApplied) {
      return msg.canvasGraph.graphToolbar.buttons.notAvailablePathlookup;
    }
    return msg.canvasGraph.graphToolbar.buttons[
      collapseNotSelectedDisabledDueToHiddenNodes
        ? 'collapseDisabledHidden'
        : 'collapseDisabledUnselected'
    ];
  };

  return (
    <GraphToolbarDropdown
      title={
        msg.canvasGraph.graphToolbar.buttons[
          ctxProps.toolbarDisabled || !hasCollapsedNodes
            ? 'collapsePristine'
            : 'collapseDirty'
        ]
      }
      disabledTitle={getDisabledTitle()}
      options={[
        {
          disabled:
            ctxProps.toolbarDisabled ||
            ctxProps.selectedVisibleNodeIds.size < 2 ||
            ctxProps.selectedHiddenNodeIds.size > 0 ||
            ctxProps.pathLookupApplied,
          label: msg.canvasGraph.graphToolbar.buttons.collapseSelected,
          onClick: () => {
            if (!ctxProps.toolbarDisabled) {
              ctxProps.dispatch(collapseSelectedNodes);
            }
          },
          tooltip:
            ctxProps.selectedHiddenNodeIds.size > 0
              ? msg.canvasGraph.graphToolbar.buttons.collapseDisabledHidden
              : undefined,
        },
        {
          disabled: collapseNotSelectedDisabled,
          label: msg.canvasGraph.graphToolbar.buttons.collapseNotSelected,
          onClick: () => {
            if (!ctxProps.toolbarDisabled) {
              ctxProps.dispatch(collapseNotSelectedNodes);
            }
          },
          tooltip:
            collapseNotSelectedDisabled &&
            collapseNotSelectedDisabledDueToHiddenNodes
              ? msg.canvasGraph.graphToolbar.buttons.collapseDisabledHidden
              : undefined,
        },
        {
          disabled:
            ctxProps.toolbarDisabled ||
            ctxProps.collapsedNodeGroupIds.every(
              (id) => !ctxProps.selectedNodeIds.has(id),
            ),
          label: msg.canvasGraph.graphToolbar.buttons.uncollapseSelected,
          onClick: () => {
            if (!ctxProps.toolbarDisabled) {
              ctxProps.dispatch(uncollapseSelectedNodes);
            }
          },
        },
        {
          disabled: ctxProps.toolbarDisabled || !hasCollapsedNodes,
          label: msg.canvasGraph.graphToolbar.buttons.uncollapseAll,
          onClick: () => {
            if (!ctxProps.toolbarDisabled) {
              ctxProps.dispatch(uncollapseAllNodes);
            }
          },
        },
      ]}
    >
      <CollapseNodesIcon title='Collapse/expand nodes' width={16} height={10} />
    </GraphToolbarDropdown>
  );
};

export const GraphToolbarCollapseMenu = memo(BareGraphToolbarCollapseMenu);
